import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  TimeoutError,
  retry,
  tap,
  throwError,
  timeout,
  timer
} from 'rxjs';
import { ApiConfig, ChatFlow } from '../../types/chat-flow.interface';
import { ApiHttpService } from 'src/app/shared/services/api-http.service';
import { environment } from 'src/environments/environment';
import { Step, StepType } from '../../types/steps.type';
import { WordOfMouthStepDataApiResponse } from '../../types/steps';
import { FocusManagementService } from 'src/app/shared/services/focus-management.service';
import { PosthogService } from 'src/app/shared/services/posthog.service';
import { getDeviceInfo } from 'src/app/shared/utils/device-info.utils';

/**
 * Ceiling for a single chatflow config attempt. The request had no bound at all
 * before, so a stalled connection left the user on a blank screen indefinitely.
 */
const FLOW_CONFIG_TIMEOUT_MS = 8_000;

/** Backoff before each retry; the length of this list is the retry count. */
const FLOW_CONFIG_RETRY_DELAYS_MS = [500, 1500];

/** Refused before reaching app code, so no feedback record can exist yet. */
const RETRIABLE_FLOW_CONFIG_STATUSES = [502, 503];

/**
 * Whether a failed chatflow config request is worth another attempt.
 *
 * `POST /v2/chatflow` creates a feedback record and is not idempotent, so only
 * failures that cannot have created one are retried. 500/504 mean app code ran
 * and already committed; a 4xx is a verdict about the URL. Status 0 and timeouts
 * are ambiguous, but they are the failure this retry exists for.
 */
function isRetriableFlowConfigError(error: unknown): boolean {
  if (error instanceof TimeoutError) {
    return true;
  }
  return (
    error instanceof HttpErrorResponse &&
    (error.status === 0 ||
      RETRIABLE_FLOW_CONFIG_STATUSES.includes(error.status))
  );
}

/** Display text of the option a user picked, keyed by language. */
export type StepAnswer = { [lang: string]: string };

interface StoredStep {
  step: number;
  answer?: StepAnswer;
}

interface StoredChatbotState {
  subdomain?: string;
  funnel_selector?: string;
  touchpoint?: string;
  funnel_id?: string;
  feedback_id?: string;
  // Set on the way out of a page load that the bot itself was navigating away
  // from, and consumed by the next load. Without it the state is inert history:
  // a manual refresh must start a fresh conversation, not resume this one.
  resume_armed?: boolean;
  // `number` entries come from a state written before answers were persisted.
  steps?: (number | StoredStep)[];
  // Steps that only ever existed in the tab (AI follow-up questions), so they
  // can be resolved again after a forced reload.
  custom_steps?: Step[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatBotFlowSaService {
  constructor(
    private apiHttp: ApiHttpService,
    private focusManagementService: FocusManagementService,
    private posthogService: PosthogService
  ) {}
  private womChannelsData: WordOfMouthStepDataApiResponse | null = null;
  private customerId: string | undefined;
  private closeNotified = false;
  // Set while a navigation the bot itself started is in flight (a WhatsApp link,
  // or the native share sheet handing the foreground to another app). Browsers
  // fire `pagehide` for those, but the user hasn't left the bot.
  private selfInitiatedNavigationPending = false;
  private static readonly PROGRESS_STORAGE_KEY = 'hypt_chatbot_progress';
  // Answers recorded in this tab, index-aligned with `userSteps$`.
  private stepAnswers: (StepAnswer | undefined)[] = [];
  // Answers read back from a previous page load, index-aligned with `userSteps$`.
  private restoredAnswers: (StepAnswer | undefined)[] = [];
  // Steps injected at runtime rather than served in the flow config - the
  // follow-up questions an AI feedback step generates. They exist only in this
  // tab, so they have to be persisted for a forced reload to resolve them.
  private customSteps: Step[] = [];

  // chat flow steps that can be removed later on when we have a backend
  private chatFlowStepsSubject = new BehaviorSubject<Step[]>([]);
  // tracking of user steps && flow config for SA
  private chatFlowSubject = new BehaviorSubject<ChatFlow | null>(null);
  chatFlow$ = this.chatFlowSubject.asObservable();
  // chat flow steps that can be removed later on when we have a backend
  private userStepsSubject = new BehaviorSubject<Step[]>([]);
  userSteps$ = this.userStepsSubject.asObservable();

  addCustomStep(step: Step) {
    this.chatFlowStepsSubject.next([
      ...this.chatFlowStepsSubject.getValue(),
      step
    ]);
    this.customSteps = [...this.customSteps, step];
    this.persistCustomSteps(this.chatFlowSubject.value?.funnel_id);
  }

  addStepToSortedStepsByStepNumber(stepNumber: number): void {
    this.focusManagementService.resetInteractionState();
    const currentSteps = this.chatFlowStepsSubject.getValue();
    const currentSortedSteps = this.userStepsSubject.getValue();
    const stepToAdd = currentSteps.find((step) => step.step === stepNumber);
    if (stepToAdd) {
      const updatedSteps = [...currentSortedSteps, stepToAdd];
      this.userStepsSubject.next(updatedSteps);
      const previousVal = this.chatFlowSubject.value;
      if (previousVal) {
        // Create a new object with the updated steps array
        const newVal = {
          ...previousVal,
          steps: [...previousVal.steps, stepToAdd]
        };
        // Emit the new value
        this.chatFlowSubject.next(newVal);
      }
      this.persistProgress(previousVal?.funnel_id, updatedSteps);
    }
  }

  setFlowConfig(config: ChatFlow): void {
    this.focusManagementService.resetInteractionState();
    this.stepAnswers = [];
    this.restoredAnswers = [];
    // Progress is only ever resumed when the previous page load armed it while
    // handing the foreground away (see `notifyPageHidden`). A plain refresh
    // leaves the flag unset and therefore starts a fresh conversation.
    const stored = this.readStoredState();
    const canResume =
      stored.resume_armed === true && stored.funnel_id === config.funnel_id;
    // Runtime-injected steps from a previous page load have to be back in the
    // flow before restoring, both so the traversed history resolves and so a
    // later jump can still find them.
    this.customSteps = canResume ? (stored.custom_steps ?? []) : [];
    this.chatFlowStepsSubject.next([...config.steps, ...this.customSteps]);
    const flow = { ...config, steps: [...config.steps] };
    const restoredSteps = canResume ? this.restoreProgress(config) : null;
    // Consume the arming either way, so refreshing a conversation that was
    // itself restored still starts over. When nothing is resumed the stale
    // history goes too, rather than lingering for a later load to pick up.
    this.writeStoredState(
      restoredSteps
        ? { resume_armed: false }
        : { resume_armed: false, steps: [], custom_steps: [] }
    );
    this.userStepsSubject.next(restoredSteps ?? [config.steps[0]]);
    this.chatFlowSubject.next(flow);
    this.customerId = this.extractCustomerIdFromSteps(config.steps);
    this.closeNotified = false;
  }

  /**
   * Notes that the bot itself is handing the foreground away - a same-tab
   * redirect (e.g. a WhatsApp link) or the native share sheet - so a `pagehide`
   * firing afterwards isn't a real "user closed the bot" signal. Stays armed
   * until the page is shown again, because the user can linger in the share
   * sheet or the target app for as long as they like.
   */
  notifyRedirectStarted(): void {
    this.selfInitiatedNavigationPending = true;
  }

  /**
   * The document is being torn down. A navigation the bot itself started being
   * in flight means this is the OS handoff discarding the page (Samsung Internet
   * does this for a WhatsApp App Link or the native share sheet), so the next
   * load of this funnel is a forced reload whose progress has to come back.
   *
   * This is the only signal that separates that from a manual refresh: a refresh
   * has no bot-initiated navigation pending, so it never arms the resume. The
   * in-memory flag alone can't do it, because the reload it describes destroys
   * it.
   */
  notifyPageHidden(options: { persisted: boolean }): void {
    // Restricted to mobile because only a mobile OS handoff discards the page.
    // A desktop share sheet leaves `selfInitiatedNavigationPending` armed (it
    // never hides the page, so nothing disarms it), and without this guard the
    // user's next refresh would look like a forced reload and resume.
    if (this.selfInitiatedNavigationPending && getDeviceInfo().isMobile) {
      this.writeStoredState({ resume_armed: true });
    }
    this.notifyChatbotClosed({ persisted: options.persisted });
  }

  /**
   * The page is back in front of the user, so whatever navigation we started is
   * over and a later `pagehide` is a genuine close again.
   */
  notifyPageRestored(): void {
    this.selfInitiatedNavigationPending = false;
    this.closeNotified = false;
    // The page came back with its context intact, so no forced reload is coming.
    // Disarming here stops a resume armed on the way out from being consumed by
    // an unrelated refresh later on.
    this.writeStoredState({ resume_armed: false });
  }

  /** Answer for a step restored from a previous page load, if it had one. */
  getRestoredAnswer(index: number): StepAnswer | null {
    return this.restoredAnswers[index] ?? null;
  }

  /**
   * Records the answer the user just gave on the step at the tail of the
   * conversation, so a forced reload can render it as answered rather than
   * re-arming its options.
   */
  recordAnswerForCurrentStep(answer: StepAnswer | undefined): void {
    const steps = this.userStepsSubject.getValue();
    if (!steps.length) return;
    this.stepAnswers[steps.length - 1] = answer ?? {};
    this.persistProgress(this.chatFlowSubject.value?.funnel_id, steps);
  }

  /**
   * Returns the feedback_id from a previous /v2/chatflow call in this tab, but
   * only for a forced reload of the exact same subdomain/funnel/touchpoint — so
   * a stale or cross-funnel id is never resumed, and a manual refresh gets a
   * fresh record rather than continuing to write into the abandoned attempt.
   *
   * Read-only on purpose: `setFlowConfig` consumes the arming later in this same
   * page load, and it needs to still be set by then.
   */
  getResumableFeedbackId(
    subdomain: string,
    funnelSelector: string,
    touchpoint: string
  ): string | undefined {
    const stored = this.readStoredState();
    const normalizedTouchpoint = touchpoint || '';
    if (
      stored.resume_armed === true &&
      stored.subdomain === subdomain &&
      stored.funnel_selector === funnelSelector &&
      (stored.touchpoint || '') === normalizedTouchpoint
    ) {
      return stored.feedback_id;
    }
    return undefined;
  }

  private persistSessionIdentity(
    subdomain: string,
    funnelSelector: string,
    touchpoint: string,
    config: ChatFlow
  ): void {
    // This runs before `setFlowConfig`, so it is the last chance to compare the
    // incoming funnel against the persisted one. Progress from a different
    // funnel must be dropped here - once `funnel_id` is overwritten, the guard
    // in `restoreProgress` can no longer tell the two apart and would replay
    // another flow's history against this config.
    const isSameFunnel = this.readStoredState().funnel_id === config.funnel_id;
    this.writeStoredState({
      subdomain,
      funnel_selector: funnelSelector,
      touchpoint: touchpoint || '',
      funnel_id: config.funnel_id,
      feedback_id: config.feedback_id,
      ...(isSameFunnel
        ? {}
        : { steps: [], custom_steps: [], resume_armed: false })
    });
  }

  private persistCustomSteps(funnelId: string | undefined): void {
    if (!funnelId) return;
    this.writeStoredState({
      funnel_id: funnelId,
      custom_steps: this.customSteps
    });
  }

  private persistProgress(
    funnelId: string | undefined,
    steps: Step[]
  ): void {
    if (!funnelId) return;
    this.writeStoredState({
      funnel_id: funnelId,
      steps: steps.map((s, index) => {
        const answer = this.stepAnswers[index];
        return answer ? { step: s.step, answer } : { step: s.step };
      })
    });
  }

  private restoreProgress(config: ChatFlow): Step[] | null {
    const stored = this.readStoredState();
    if (stored.funnel_id !== config.funnel_id || !stored.steps?.length) {
      return null;
    }
    const storedSteps: StoredStep[] = stored.steps.map((entry) =>
      typeof entry === 'number' ? { step: entry } : entry
    );
    const knownSteps = [...config.steps, ...this.customSteps];
    const restoredSteps: Step[] = [];
    const answers: (StepAnswer | undefined)[] = [];
    for (const entry of storedSteps) {
      const step = knownSteps.find((s) => s.step === entry.step);
      // A step we can no longer resolve ends the restore. Keeping the history up
      // to that point costs the user the tail, whereas dropping everything sends
      // them back to step 1.
      if (!step) break;
      restoredSteps.push(step);
      answers.push(entry.answer);
    }
    if (!restoredSteps.length) return null;
    // The answer on the last step means the page died between the click and the
    // delayed step advance, so no following step was ever stored. Keep that step
    // interactive - rendering it as answered would leave the user with nothing
    // to act on and no way forward.
    answers[answers.length - 1] = undefined;
    this.stepAnswers = [...answers];
    this.restoredAnswers = answers;
    return restoredSteps;
  }

  private readStoredState(): StoredChatbotState {
    try {
      const stored = sessionStorage.getItem(
        ChatBotFlowSaService.PROGRESS_STORAGE_KEY
      );
      return stored ? (JSON.parse(stored) as StoredChatbotState) : {};
    } catch {
      return {};
    }
  }

  private writeStoredState(partial: StoredChatbotState): void {
    try {
      const current = this.readStoredState();
      sessionStorage.setItem(
        ChatBotFlowSaService.PROGRESS_STORAGE_KEY,
        JSON.stringify({ ...current, ...partial })
      );
    } catch {
      // sessionStorage can be unavailable (e.g. private browsing) - progress
      // persistence is a best-effort UX improvement, not a hard requirement.
    }
  }

  private extractCustomerIdFromSteps(steps: Step[]): string | undefined {
    for (const step of steps) {
      const url = (step as { api?: { url?: string } }).api?.url;
      const match = url?.match(/\/customer\/([^/?#]+)/);
      if (match) return match[1];
    }
    return undefined;
  }

  private buildFeedbackUrl(companyId: string, feedbackId: string): string {
    return (
      `${environment.apiServers.apiEndpointFeedback}/v2/customer/` +
      `${encodeURIComponent(companyId)}/feedback/${encodeURIComponent(feedbackId)}`
    );
  }

  getCurrentFunnelId(): string | undefined {
    return this.chatFlowSubject.value?.funnel_id;
  }

  getCurrentStepId(): number | undefined {
    const steps = this.userStepsSubject.getValue();
    return steps.length ? steps[steps.length - 1].step_id : undefined;
  }

  notifyChatbotClosed(options?: { persisted?: boolean }): void {
    if (this.closeNotified) return;
    // Entering the back/forward cache: the page is frozen, not gone.
    if (options?.persisted) return;
    // A share sheet or redirect we started ourselves is still in flight.
    if (this.selfInitiatedNavigationPending) return;
    const companyId = this.customerId;
    const feedbackId = this.chatFlowSubject.value?.feedback_id;
    if (!companyId || !feedbackId) return;
    this.closeNotified = true;

    const url = `${this.buildFeedbackUrl(companyId, feedbackId)}/close_chatbot`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    const distinctId = this.posthogService.getDistinctId();
    const funnelId = this.getCurrentFunnelId();
    const stepId = this.getCurrentStepId();
    if (distinctId) headers['X-PostHog-Distinct-Id'] = distinctId;
    if (funnelId) headers['X-Funnel-Id'] = funnelId;
    if (stepId !== undefined) headers['X-Step-Id'] = String(stepId);

    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers,
      body: '{}'
    });
  }

  notifyStepSkipped(feedbackSlots = 0) {
    const companyId = this.customerId;
    const feedbackId = this.chatFlowSubject.value?.feedback_id;
    if (!companyId || !feedbackId) return EMPTY;
    return this.apiHttp.post<unknown>(
      `${this.buildFeedbackUrl(companyId, feedbackId)}/skip_step`,
      { feedback_slots: feedbackSlots }
    );
  }

  updateFeedbackApi<T extends StepType>(config: ApiConfig<T>) {
    console.log('Updating feedback API with config:', config);
    const urlObj = new URL(config.url);
    // Extract the path and query from the URL
    const pathWithQuery =
      environment.apiServers.apiEndpointFeedback +
      urlObj.pathname +
      urlObj.search;

    if (config.method.toUpperCase() === 'POST') {
      return this.apiHttp.post<any>(pathWithQuery, config.data);
    } else if (config.method === 'PATCH') {
      return this.apiHttp.patch<any>(pathWithQuery, config.data);
    } else {
      // Optionally handle unsupported methods
      throw new Error(`Unsupported method: ${config.method}`);
    }
  }

  // New method to fetch flow config from API
  fetchFlowConfigFromApi(
    subdomain: string,
    funnel_selector: string,
    params: { [key: string]: string },
    lang?: string,
    resumeFeedbackId?: string
  ): Observable<ChatFlow> {
    const { touchpoint, test, ...otherParams } = params;
    let data: any = {
      funnel_selector: funnel_selector,
      subdomain: subdomain,
      touchpoint: touchpoint || '',
      chatbot_query_params: { ...(otherParams || {}) }
    };
    if (lang) data.language = lang;
    if (touchpoint) data.touchpoint = touchpoint;
    if (test) data.is_testing = test;
    if (resumeFeedbackId)
      data.chatbot_query_params['feedback_id'] = resumeFeedbackId;
    if (data.chatbot_query_params['trigger'])
      data.trigger = data.chatbot_query_params['trigger'];
    return this.apiHttp
      .post<ChatFlow>(
        `${environment.apiServers.apiEndpointFeedback}/v2/chatflow`,
        data
      )
      .pipe(
        // The usual entry point is a QR-code scan on mobile data, so one dropped
        // request should not cost the whole session. UI-CHAT-BOT-12 showed this
        // POST failing with status 0 just 271ms after navigation and never being
        // retried — the user got a dead "funnel not found" screen instead.
        // `timeout` applies per attempt, so a stalled request is abandoned and
        // retried rather than hanging. Worst case (three successive stalls) the
        // error page is ~26s out; the common instant-failure case is ~2s.
        timeout(FLOW_CONFIG_TIMEOUT_MS),
        retry({
          count: FLOW_CONFIG_RETRY_DELAYS_MS.length,
          delay: (error, retryCount) =>
            isRetriableFlowConfigError(error)
              ? timer(FLOW_CONFIG_RETRY_DELAYS_MS[retryCount - 1])
              : throwError(() => error)
        }),
        tap((response) =>
          this.persistSessionIdentity(
            subdomain,
            funnel_selector,
            touchpoint || '',
            response
          )
        )
      );
  }
  setWomChannels(data: WordOfMouthStepDataApiResponse): void {
    this.womChannelsData = data;
  }
  getWomChannels(): WordOfMouthStepDataApiResponse | null {
    return this.womChannelsData;
  }
}
